import * as React from "react"
import { cn } from "./utils"

/**
 * Plain-React Select mit Kompatibilität zu:
 * <Select value onValueChange>
 *   <SelectTrigger id="..."><SelectValue placeholder="..." /></SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="x">Label</SelectItem>
 *     ...
 *   </SelectContent>
 * </Select>
 */

type Option = { value: string; label: React.ReactNode }

type Ctx = {
    value?: string
    setValue: (v: string) => void
    options: Option[]
    registerOption: (opt: Option) => void
}

const SelectCtx = React.createContext<Ctx | null>(null)
const useSelectCtx = () => {
    const ctx = React.useContext(SelectCtx)
    if (!ctx)
        throw new Error("Select components must be used within <Select />")
    return ctx
}

interface RootProps {
    value?: string
    defaultValue?: string
    onValueChange?: (v: string) => void
    children?: React.ReactNode
}
function Select({ value, defaultValue, onValueChange, children }: RootProps) {
    const isControlled = value !== undefined
    const [inner, setInner] = React.useState<string>(defaultValue ?? "")

    const [options, setOptions] = React.useState<Option[]>([])
    const registerOption = React.useCallback((opt: Option) => {
        setOptions((prev) => {
            if (prev.some((o) => o.value === opt.value)) return prev
            return [...prev, opt]
        })
    }, [])

    const current = isControlled ? value! : inner
    const setValue = (v: string) => {
        if (!isControlled) setInner(v)
        onValueChange?.(v)
    }

    const ctx: Ctx = React.useMemo(
        () => ({ value: current, setValue, options, registerOption }),
        [current, options, registerOption]
    )

    return <SelectCtx.Provider value={ctx}>{children}</SelectCtx.Provider>
}

/** Trigger rendert das native <select> */
type TriggerProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
    size?: "sm" | "default"
}
function SelectTrigger({ className, size, children, ...props }: TriggerProps) {
    const { value, setValue, options } = useSelectCtx()
    const dataSize = size || "default"
    return (
        <select
            data-slot="select-trigger"
            data-size={dataSize}
            className={cn(
                "border-input data-[placeholder]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 \
         dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center gap-2 rounded-4xl border bg-transparent px-3 py-2 \
         text-sm shadow-xs outline-none disabled:cursor-not-allowed disabled:opacity-50 h-10",
                className
            )}
            value={value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            {...props}
        >
            {/* Optionaler Placeholder aus <SelectValue> wird NICHT benötigt */}
            {/* Optionen aus <SelectItem> */}
            {options.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                    {opt.label as any}
                </option>
            ))}
            {/* children werden ignoriert, damit Markup kompatibel bleibt */}
            {children ? <></> : null}
        </select>
    )
}

/** Content nur zum Mounten der Items – bleibt unsichtbar */
function SelectContent({
    className,
    children,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            data-slot="select-content"
            className={cn("hidden", className)}
            aria-hidden
            {...props}
        >
            {children}
        </div>
    )
}

/** Wert-Platzhalter – in der Plain-Variante nicht erforderlich */
function SelectValue(_: { placeholder?: string }) {
    return null
}

/** Item registriert sich als Option; rendert selbst nichts */
type ItemProps = { value: string; children: React.ReactNode } & Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onSelect"
>
function SelectItem({ value, children }: ItemProps) {
    const { registerOption } = useSelectCtx()
    React.useEffect(() => {
        registerOption({ value: String(value), label: children })
    }, [value, children, registerOption])
    return null
}

/** Group/Label/ScrollButtons/Separator – No-Op/Kompatibilität */
const SelectGroup: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    children,
}) => <>{children}</>
const SelectLabel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    children,
}) => <>{children}</>
const SelectScrollUpButton: React.FC = () => null
const SelectScrollDownButton: React.FC = () => null
const SelectSeparator: React.FC = () => null

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
}
